import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, MessageSquarePlus } from "lucide-react";
import { api } from "@/lib/api";

const CATEGORIES = [
  { value: "confusing", label: "Something was confusing" },
  { value: "broken", label: "Something didn't work" },
  { value: "privacy", label: "Privacy concern" },
  { value: "incorrect", label: "Incorrect information" },
  { value: "suggestion", label: "Feature suggestion" },
  { value: "other", label: "Other" },
];

/** Phase 12 — HELP US IMPROVE WITNESSYOURVOTE. Separate from Report an Error
 * (which deals with factual errors). Page is recorded automatically. */
export const BetaFeedbackButton = ({ variant = "outline", size = "sm", className = "", dark = false }) => {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [comment, setComment] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/public/beta/feedback", {
        category,
        comment: comment.trim(),
        page: window.location.pathname,
        email: email.trim() || null,
        website: "",
      });
      setSent(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send your feedback.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setSent(false); setComment(""); setCategory(""); setEmail(""); } }}>
      <DialogTrigger asChild>
        <Button variant={variant} size={size}
                className={`${dark ? "border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white bg-transparent" : ""} ${className}`}
                data-testid="beta-feedback-open">
          <MessageSquarePlus className="h-3.5 w-3.5 mr-1.5" /> SEND BETA FEEDBACK
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Help us improve WitnessYourVote</DialogTitle>
          <DialogDescription>
            We're in BETA — your feedback directly shapes the platform before launch.
            For factual errors in published information, please use Report an Error instead.
          </DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="py-4" data-testid="beta-feedback-success">
            <p className="font-heading font-semibold text-foreground inline-flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-accent" /> Thank you!
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Your feedback has been recorded and will be reviewed by the team.
            </p>
            <Button className="mt-4" variant="outline" onClick={() => setOpen(false)} data-testid="beta-feedback-close">Close</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">What kind of feedback is this?</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1" data-testid="beta-feedback-category">
                  <SelectValue placeholder="Choose a category…" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value} data-testid={`beta-feedback-cat-${c.value}`}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Your feedback</Label>
              <Textarea className="mt-1" rows={4} maxLength={2000} value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Tell us what happened, what confused you, or what you'd like to see…"
                        data-testid="beta-feedback-comment" />
            </div>
            <div>
              <Label className="text-xs">Email <span className="text-muted-foreground">(optional — only if you'd like a reply)</span></Label>
              <Input className="mt-1" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     data-testid="beta-feedback-email" />
            </div>
            <p className="text-[10px] text-muted-foreground">
              The page you're on ({window.location.pathname}) is recorded automatically to help us find the issue.
            </p>
            <Button onClick={submit} disabled={busy || !category || comment.trim().length < 5}
                    className="bg-primary hover:bg-primary/90 w-full" data-testid="beta-feedback-submit">
              {busy ? "Sending…" : "Send feedback"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
